import type { TFunction } from 'i18next'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import { EVENT_GROUP_MAP } from './auditLogConstants'

interface ExportParams {
  dateFrom: Date
  dateTo: Date
  eventGroups?: string[]
  actorAdminIds?: string[]
}

export interface AuditExportFilterLabels {
  dateRange: string
  eventGroup?: string
  actor?: string
}

interface AuditLogRow {
  id: string
  event_type: string
  actor_admin_id: string | null
  target_entity_type: string | null
  target_entity_id: string | null
  target_entity_name: string | null
  changes: Record<string, unknown> | null
  reason: string | null
  note: string | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
  admin_accounts: { name: string; email: string } | null
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function toTitleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatCsvValue(val: unknown): string {
  if (val && typeof val === 'object' && 'name' in val) {
    const obj = val as { name: string; id?: string }
    return obj.id ? `${obj.name} (${obj.id})` : obj.name
  }
  if (typeof val === 'string') return toTitleCase(val)
  return String(val ?? 'N/A')
}

function formatChanges(changes: Record<string, unknown> | null): string {
  if (!changes) return ''
  return Object.entries(changes)
    .map(([key, val]) => {
      const label = toTitleCase(key)
      if (val === true) return label
      if (val && typeof val === 'object' && 'before' in val && 'after' in val) {
        const v = val as { before: unknown; after: unknown }
        return `${label}: ${formatCsvValue(v.before)} → ${formatCsvValue(v.after)}`
      }
      return `${label}: ${String(val)}`
    })
    .join('; ')
}

function buildFilterLines(labels: AuditExportFilterLabels, t: TFunction): string[] {
  const lines: string[] = []
  lines.push(`${t('auditLog.csv.filterDateRange')}: ${labels.dateRange}`)
  if (labels.eventGroup) {
    lines.push(`${t('auditLog.filters.action')}: ${labels.eventGroup}`)
  }
  if (labels.actor) {
    lines.push(`${t('auditLog.columns.actionBy')}: ${labels.actor}`)
  }
  return lines
}

export async function exportAuditLogCsv(
  params: ExportParams,
  filterLabels: AuditExportFilterLabels,
  t: TFunction,
): Promise<void> {
  const { dateFrom, dateTo, eventGroups, actorAdminIds } = params

  let query = supabase
    .from('audit_log')
    .select(
      'id, event_type, actor_admin_id, target_entity_type, target_entity_id, target_entity_name, changes, reason, note, ip_address, user_agent, created_at, admin_accounts(name, email)',
    )
    .gte('created_at', dateFrom.toISOString())
    .lte('created_at', dateTo.toISOString())
    .order('created_at', { ascending: false })

  if (eventGroups && eventGroups.length > 0) {
    const eventTypes = eventGroups.flatMap((g) => EVENT_GROUP_MAP[g] ?? [])
    if (eventTypes.length > 0) query = query.in('event_type', eventTypes)
  }

  if (actorAdminIds && actorAdminIds.length > 0) {
    query = query.in('actor_admin_id', actorAdminIds)
  }

  const { data, error } = await query

  if (error) throw error

  const rows = data as unknown as AuditLogRow[]

  // Everything from here on is local CSV-building and browser download
  // mechanics — no backend round trip — so failures are invisible anywhere
  // else and worth reporting.
  try {
    const filterLines = buildFilterLines(filterLabels, t)

    const headers = ['Timestamp', 'Action', 'Target', 'Target (ID)', 'Details', 'Reason', 'Note', 'Action by', 'Action by (email)']
    const csvRows = rows.map((row) => [
      row.created_at,
      t(`auditLog.eventTypes.${row.event_type}` as never),
      row.target_entity_name ?? row.target_entity_type ?? '',
      row.target_entity_id ?? '',
      formatChanges(row.changes),
      row.reason ? t(`common.adjustmentReasons.${row.reason}` as never) : '',
      row.note ?? '',
      row.admin_accounts?.name ?? '',
      row.admin_accounts?.email ?? '',
    ])

    const csv = [
      ...filterLines.map(escapeCsvField),
      '',
      '',
      headers.map(escapeCsvField).join(','),
      ...csvRows.map((row) => row.map(escapeCsvField).join(',')),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  } catch (err) {
    reportUnexpectedError(err)
    throw err
  }
}